export type PickerUser = {
  id: string;
  fullName?: string | null;
  email?: string | null;
  status?: string | null;
  isOwner?: boolean | null;
  roles?: Array<{ role?: { slug?: string | null } | null } | null> | null;
};

function statusRank(status?: string | null): number {
  if (status === 'ACTIVE') return 0;
  if (status === 'PENDING') return 1;
  return 2;
}

/** One option per userId; prefer the live membership if a twin slipped through. */
export function uniqueUsersById<T extends PickerUser>(users: T[] | undefined | null): T[] {
  if (!users?.length) return [];
  const byId = new Map<string, T>();
  for (const user of users) {
    if (!user?.id) continue;
    const previous = byId.get(user.id);
    if (!previous || statusRank(user.status) < statusRank(previous.status)) {
      byId.set(user.id, user);
    }
  }
  return [...byId.values()];
}

function displayName(user: PickerUser): string {
  return (user.fullName ?? '').trim() || user.email || 'Unknown';
}

/**
 * Same-name colleagues (two User rows) must not look identical in a picker.
 * Distinct emails are the only safe distinguisher we can show.
 */
export function userPickerLabel(user: PickerUser, peers: PickerUser[]): string {
  const name = displayName(user);
  const key = name.toLowerCase();
  const collisions = peers.filter((peer) => displayName(peer).toLowerCase() === key);
  if (collisions.length > 1 && user.email) {
    return `${name} (${user.email})`;
  }
  return name;
}

export function isAccountOwner(user: PickerUser | null | undefined): boolean {
  if (!user) return false;
  if (user.isOwner === true) return true;
  return (user.roles ?? []).some((entry) => entry?.role?.slug === 'role-owner');
}

/**
 * Assignment pickers always include the Account Owner — they may perform
 * any role. Owners are listed first; duplicates by id are collapsed.
 */
export function withOwnersAlways<T extends PickerUser>(
  roleUsers: T[] | undefined | null,
  owners: T[] | undefined | null,
  currentUser?: T | null,
): T[] {
  const extras: T[] = [...(owners ?? [])];
  if (currentUser && isAccountOwner(currentUser)) {
    extras.unshift(currentUser);
  }
  return uniqueUsersById([...extras, ...(roleUsers ?? [])]);
}
